
import { useState, useMemo } from "react";

export type SortField = 'user' | 'lastAccess' | 'casos' | 'conversations' | 'plan' | 'storage' | 'approved';
export type SortDirection = 'asc' | 'desc';

interface UseSortableTableProps {
  data: any[];
  getSubscriptionForUser: (userId: string) => any;
}

export const useSortableTable = ({ data, getSubscriptionForUser }: UseSortableTableProps) => {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'user':
          aValue = (a.display_name || a.email || '').toLowerCase();
          bValue = (b.display_name || b.email || '').toLowerCase();
          break;
        
        case 'lastAccess':
          aValue = a.last_access ? new Date(a.last_access).getTime() : 0;
          bValue = b.last_access ? new Date(b.last_access).getTime() : 0;
          break;
        
        case 'casos':
          aValue = a.casos_count || 0;
          bValue = b.casos_count || 0;
          break;
        
        case 'conversations':
          aValue = a.conversations_count || 0;
          bValue = b.conversations_count || 0;
          break;
        
        case 'plan':
          const getPlanValue = (userId: string) => {
            const subscription = getSubscriptionForUser(userId);
            const planType = subscription?.plan_type || 'free';
            return planType === 'pro' ? 3 : planType === 'basic' ? 2 : 1;
          };
          aValue = getPlanValue(a.id);
          bValue = getPlanValue(b.id);
          break;
        
        case 'storage':
          const getStorageUsage = (userId: string) => {
            const subscription = getSubscriptionForUser(userId);
            return subscription?.current_usage || 0;
          };
          aValue = getStorageUsage(a.id);
          bValue = getStorageUsage(b.id);
          break;
        
        case 'approved':
          aValue = a.status_chat !== false ? 1 : 0;
          bValue = b.status_chat !== false ? 1 : 0;
          break;
        
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortField, sortDirection, getSubscriptionForUser]);

  return {
    sortedData,
    sortField,
    sortDirection,
    toggleSort
  };
};
